import React, { useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// notification
import { notifyError, notifySuccess } from '../Notification';

// set up amplify config
import { Amplify } from 'aws-amplify';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import awsconfig from '../../../src/aws-exports';

import './auth.css';

Amplify.configure(awsconfig);

const ForgotPassword = ({ username = '', onClose }) => {
    const [email, setEmail] = useState(username);
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('request'); // 'request' or 'reset'

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    };

    const handleVerificationCodeChange = (e) => {
        setVerificationCode(e.target.value);
    };

    const handleNewPasswordChange = (e) => {
        setNewPassword(e.target.value);
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
    };

    const handleRequestCode = async (e) => {
        e.preventDefault();
        
        if (!email) {
            notifyError('Please enter your email address');
            return;
        }

        try {
            setLoading(true);
            await resetPassword({ username: email });
            notifySuccess('Verification code sent to your email');
            setStep('reset');
        } catch (error) {
            console.error('Error requesting password reset:', error);
            notifyError(error.message || 'Failed to send verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        if (!verificationCode) {
            notifyError('Please enter the verification code');
            return;
        }

        if (!newPassword) {
            notifyError('Please enter a new password');
            return;
        }

        if (newPassword !== confirmPassword) {
            notifyError('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            notifyError('Password must be at least 8 characters long');
            return;
        }

        try {
            setLoading(true);
            await confirmResetPassword({
                username: email,
                confirmationCode: verificationCode,
                newPassword: newPassword
            });
            notifySuccess('Password reset successfully! You can now sign in with your new password.');
            onClose();
        } catch (error) {
            console.error('Error resetting password:', error);
            notifyError(error.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToRequest = () => {
        setStep('request');
        setVerificationCode('');
        setNewPassword('');
        setConfirmPassword('');
    };

    return (
        <div className="forgot-password-container">
            {step === 'request' ? (
                <div className="forgot-password-form">
                    <div className="forgot-password-header">
                        <h2 className="forgot-password-title">Reset your password</h2>
                        <p className="forgot-password-subtitle">
                            Enter your email address and we'll send you a verification code
                        </p>
                    </div>

                    <form onSubmit={handleRequestCode}>
                        <div className="form-group">
                            <label className="form-label" htmlFor="reset-email">
                                Email address
                            </label>
                            <input
                                id="reset-email"
                                type="email"
                                className="form-input"
                                placeholder="Enter your email"
                                value={email}
                                onChange={handleEmailChange}
                                required
                            />
                        </div>

                        <button 
                            type="submit" 
                            className="submit-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Sending...
                                </>
                            ) : 'Send verification code'}
                        </button>
                    </form>

                    <div className="forgot-password-links">
                        <button 
                            type="button"
                            className="auth-link-btn" 
                            onClick={onClose}
                        >
                            Back to sign in
                        </button>
                    </div>
                </div>
            ) : (
                <div className="forgot-password-form">
                    <div className="forgot-password-header">
                        <h2 className="forgot-password-title">Create new password</h2>
                        <p className="forgot-password-subtitle">
                            Enter the verification code sent to <span className="highlight-email">{email}</span>
                        </p>
                    </div>

                    <form onSubmit={handleResetPassword}>
                        <div className="form-group">
                            <label className="form-label" htmlFor="verification-code">
                                Verification code
                            </label>
                            <input
                                id="verification-code"
                                type="text"
                                className="form-input verification-input"
                                placeholder="Enter verification code"
                                value={verificationCode}
                                onChange={handleVerificationCodeChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="new-password">
                                New password
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    id="new-password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    className="form-input"
                                    placeholder="Create a new password"
                                    value={newPassword}
                                    onChange={handleNewPasswordChange}
                                    minLength={8}
                                    required
                                />
                                <button 
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    <FontAwesomeIcon icon={showNewPassword ? faEye : faEyeSlash} />
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="confirm-password">
                                Confirm new password
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    id="confirm-password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className="form-input"
                                    placeholder="Confirm your new password"
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    minLength={8}
                                    required
                                />
                                <button 
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
                                </button>
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            className="submit-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Resetting...
                                </>
                            ) : 'Reset password'}
                        </button>
                    </form>

                    <div className="forgot-password-links">
                        <button 
                            type="button"
                            className="auth-link-btn" 
                            onClick={handleBackToRequest}
                        >
                            Didn't receive code? Try again
                        </button>
                        <button 
                            type="button"
                            className="auth-link-btn" 
                            onClick={onClose}
                        >
                            Back to sign in
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ForgotPassword;